// Shared royalty utilities

import type { RoyaltyBalance, RoyaltyChargeType } from '@/types'
import { contractNumberFromAscii } from './royaltyPaymentReference'

type ChargeCoverageFields = Pick<
  RoyaltyBalance,
  | 'offset_amount' | 'offset_amount_rub'
  | 'written_off_amount' | 'written_off_amount_rub'
  | 'rb_tax_offset_amount' | 'rb_tax_offset_amount_rub'
>

/**
 * Покрытие начисления без денег: зачёт возврата (§20) + списание долга (P-34) + зачёт РБ.
 * Все три уже вычтены из royalty_balance.debt_amount(_rub). Единственная реализация для
 * экранов, считающих долг сами: добавление четвёртой составляющей в одну копию и
 * забывание во второй давало бы разный долг на карточке серии и франчайзи.
 */
export function getChargeNonCashCoverage(charge: ChargeCoverageFields, native: boolean): number {
  return native
    ? Number(charge.offset_amount ?? 0) + Number(charge.written_off_amount ?? 0) + Number(charge.rb_tax_offset_amount ?? 0)
    : Number(charge.offset_amount_rub ?? 0) + Number(charge.written_off_amount_rub ?? 0) + Number(charge.rb_tax_offset_amount_rub ?? 0)
}

/**
 * Долг в валюте обязательства. Считается здесь, а не берётся из royalty_balance.debt_amount:
 * нативная разноска нужна точная, из аллокаций. Не-денежное покрытие вычитаем из витрины —
 * там нативные суммы посчитаны той же формулой, что в royalty_charge_debt_remaining_v.
 */
export function getNativeDebtAmountForCharge(
  charge: Pick<RoyaltyBalance, 'charge_id' | 'royalty_amount'> & ChargeCoverageFields,
  allocatedNativeByChargeId: Map<number, number>
): number {
  return Math.max(0, Number(charge.royalty_amount ?? 0)
    - (allocatedNativeByChargeId.get(charge.charge_id) ?? 0)
    - getChargeNonCashCoverage(charge, true))
}

const MONTH_NAMES_MAP: [string, number][] = [
  ['январ', 1], ['феврал', 2], ['март', 3], ['апрел', 4],
  ['май', 5], ['мая', 5], ['июн', 6], ['июл', 7], ['август', 8],
  ['сентябр', 9], ['октябр', 10], ['ноябр', 11], ['декабр', 12],
]

const LOOKALIKE_LETTER_MAP: Record<string, string> = {
  a: 'а',
  b: 'в',
  c: 'с',
  e: 'е',
  h: 'н',
  k: 'к',
  m: 'м',
  o: 'о',
  p: 'р',
  t: 'т',
  x: 'х',
  y: 'у',
}

export const JAMES_BILGTON_INN = '7728481743'
const BELARUS_BANK_ID_RE = /BY\d{2}[A-Z]{4}[A-Z0-9]{20,}/i
// Белорусский банк печатает учётный номер плательщика прямо в имени контрагента:
// «УНН692228658 ООО К2 ПРОДЖЕКТ //223053, БЕЛ». УНП/УНН — ровно 9 цифр.
const BELARUS_TAX_ID_IN_TEXT_RE = /(?:УНП|УНН)\s*(\d{9})(?!\d)/i

export function parsePurposePeriod(purpose: string): { month: number | null; year: number | null } {
  if (!purpose) return { month: null, year: null }
  const low = purpose.toLowerCase()

  // "за март 2026", "за ноябрь 2025 г", "за период август 2026", "за отчетный период май 2026"
  const m1 = low.match(/за\s+(?:отчетн\S*\s+)?(?:период[а-я]*\s+)?(январ[ья]?|феврал[ья]?|март[а]?|апрел[ья]?|ма[йя]|июн[ья]?|июл[ья]?|август[а]?|сентябр[ья]?|октябр[ья]?|ноябр[ья]?|декабр[ья]?)\s*(\d{4})/i)
  if (m1) {
    const word = m1[1].toLowerCase()
    const year = parseInt(m1[2])
    for (const [prefix, num] of MONTH_NAMES_MAP) {
      if (word.startsWith(prefix)) return { month: num, year }
    }
  }

  // "за 01.2025" / "за 1.2025"
  const m2 = low.match(/за\s+(?:отчетн\S*\s+)?(?:период[а-я]*\s+)?(\d{1,2})\.(\d{4})/)
  if (m2) return { month: parseInt(m2[1]), year: parseInt(m2[2]) }

  // "за 01.04.2026" / "за 1.4.2026" — day.month.year, but for royalty
  // purposes this usually means "for April 2026".
  const m3 = low.match(/за\s+(?:отчетн\S*\s+)?(?:период[а-я]*\s+)?(\d{1,2})\.(\d{1,2})\.(\d{4})/)
  if (m3) return { month: parseInt(m3[2]), year: parseInt(m3[3]) }

  // Назначение формата LVREF: «LVREF… ROY 08.2026 DKK…».
  const m4 = purpose.match(/(?<![A-Za-z])(?:ROY|SRY|SAF|MRY|CRM|FRY)\s+(0[1-9]|1[0-2])\.(20\d{2})(?!\d)/)
  if (m4) return { month: parseInt(m4[1]), year: parseInt(m4[2]) }

  return { month: null, year: null }
}

export function normalizeContractNumberForMatching(value: string | null | undefined): string {
  if (!value) return ''

  return value
    .trim()
    .toLowerCase()
    .replace(/[–—−]/g, '-')
    .replace(/\s*-\s*/g, '-')
    .replace(/\s+/g, '')
    .replace(/[a-zа-я]/g, (char) => LOOKALIKE_LETTER_MAP[char] ?? char)
}

export function normalizeInnForMatching(value: string | null | undefined): string {
  const raw = (value ?? '').trim()
  if (!raw) return ''

  const belarusId = raw.match(BELARUS_BANK_ID_RE)?.[0]
  if (belarusId) return belarusId.toUpperCase()

  return raw.replace(/\D/g, '')
}

export function extractBelarusTaxIdFromText(value: string | null | undefined): string | null {
  return (value ?? '').match(BELARUS_TAX_ID_IN_TEXT_RE)?.[1] ?? null
}

export function extractBankIdentifierFromText(value: string | null | undefined): string | null {
  const raw = (value ?? '').trim()
  if (!raw) return null

  const belarusId = raw.match(BELARUS_BANK_ID_RE)?.[0]
  if (belarusId) return belarusId.toUpperCase()

  // УНП/УНН читается ДО склейки всех цифр строки: у белорусов имя контрагента
  // содержит и учётный номер, и почтовый индекс с адресом, поэтому «все цифры»
  // давали мусор («УНН692228658 … //223053, БЕЛ» → «6922286582223053»), и строка
  // с пустой колонкой ИНН не матчилась ни на что.
  const belarusTaxId = extractBelarusTaxIdFromText(raw)
  if (belarusTaxId) return belarusTaxId

  const digits = raw.replace(/\D/g, '')
  return digits || null
}

export function isJamesBilgtonInn(value: string | null | undefined): boolean {
  return normalizeInnForMatching(value) === JAMES_BILGTON_INN
}

function normalizeExtractedContractNumber(value: string): string {
  return value
    .trim()
    .replace(/\?/g, '')
    .replace(/\s+/g, ' ')
    .replace(/\s*([\-–—−])\s*/g, '$1')
    .replace(/^(\d+)([a-zа-я])(?=$|-)/i, (_match, digits: string, letter: string) =>
      /^[aа]$/i.test(letter) ? `${digits}${letter}` : `${digits}-${letter}`
    )
    .replace(/[.,;:)\]]+$/g, '')
}

function stripIgnoredSubscriptionPaymentRefs(purpose: string): string {
  return purpose.replace(
    /(?:роялти\s+за\s+подписки|агентское\s+вознаграждение\s+за\s+подписки)\s*\(\s*(?:№|#|n(?:o|о)?\??)\s*\d+[0-9a-zа-я]*(?:\s*[-–—−]\s*[0-9a-zа-я]+)*\s*\)/gi,
    ''
  )
}

function hasExplicitDkkMarker(value: string | null | undefined): boolean {
  return /(^|[^\p{L}\p{N}_])дкк($|[^\p{L}\p{N}_])/iu.test(value ?? '')
}

export function getContractNumberRoot(value: string): string {
  return normalizeContractNumberForMatching(value).match(/^\d+/)?.[0] ?? ''
}

function getContractNumberSpecificity(value: string): number {
  const normalized = normalizeContractNumberForMatching(value)
  if (!normalized) return 0

  const segments = normalized.split('-').filter(Boolean).length
  const digitCount = (normalized.match(/\d/g) ?? []).length
  return segments * 100 + digitCount * 10 + normalized.length
}

export function extractDkk(purpose: string): string | null {
  if (!purpose) return null
  const searchablePurpose = stripIgnoredSubscriptionPaymentRefs(purpose)

  // Назначение формата LVREF: «… DKK058-P». Номер латиницей разбирается обратной
  // таблицей §3.2 (058-P → 058-П, 263-R → 263-Р), а не визуальным нормализатором.
  const asciiDkk = searchablePurpose.match(/(?<![A-Za-z])DKK\s*(\d[0-9A-Za-z/-]*)/i)?.[1]
  if (asciiDkk) return contractNumberFromAscii(asciiDkk)

  // Головной сегмент допускает дробь («185/1», «338/1-Н», «519/1-Н» — четыре таких
  // номера в реестре), дальше идут обычные сегменты через дефис. Дробная часть
  // берётся ТОЛЬКО без пробелов и только если за ней не следует ещё один слэш:
  // в назначениях платежей встречаются даты «;28/06/2026» и ссылки на чужие
  // документы («ДС №26/12/2024»), и жадный слэш утащил бы год в номер договора.
  const numberPattern = String.raw`(\d+[0-9a-zа-я]*(?:/(?!\d+/)\d+[0-9a-zа-я]*)?(?:\s*[-–—−]\s*[0-9a-zа-я]+)*)`
  // «N»/«No» — пометка номера только отдельным словом: иначе «Payout in 5 Days»
  // в назначениях Zbooni давало «n 5» и отдавало строку договору № 5.
  const markerPattern = String.raw`(?:№|#|(?<![0-9a-zа-яё_])n(?:o|о)?\??)`
  const patterns = [
    new RegExp(String.raw`дкк\s*(?:${markerPattern}\s*)?${numberPattern}`, 'i'),
    new RegExp(String.raw`договор[а-я]*\s+коммерческой\s+концессии\s*(?:${markerPattern}\s*)?${numberPattern}`, 'i'),
    new RegExp(String.raw`договор[а-я]*\s*(?:${markerPattern}\s*)?${numberPattern}`, 'i'),
    new RegExp(String.raw`${markerPattern}\s*${numberPattern}`, 'i'),
  ]

  for (const pattern of patterns) {
    const extracted = searchablePurpose.match(pattern)?.[1]?.trim()
    if (extracted) return normalizeExtractedContractNumber(extracted)
  }

  return null
}

export function resolveDkkForMatching(
  parsedDkk: string | null | undefined,
  paymentPurpose: string | null | undefined
): string {
  const parsed = (parsedDkk ?? '').trim()
  const extracted = extractDkk(paymentPurpose ?? '') ?? ''

  if (!parsed) return extracted
  if (!extracted) return parsed

  const normalizedParsed = normalizeContractNumberForMatching(parsed)
  const normalizedExtracted = normalizeContractNumberForMatching(extracted)

  if (normalizedExtracted === normalizedParsed) {
    return extracted.length >= parsed.length ? extracted : parsed
  }

  // Older imports could store an order/subscription number from "(№12345)"
  // before the real "по ДКК №..." marker. When the purpose has an explicit DKK,
  // the freshly parsed contract number is the safer source for matching.
  if (hasExplicitDkkMarker(paymentPurpose)) {
    return extracted
  }

  const parsedRoot = getContractNumberRoot(parsed)
  const extractedRoot = getContractNumberRoot(extracted)
  const sameRoot = !!parsedRoot && parsedRoot === extractedRoot

  // Prefer the freshly re-parsed value when it points to the same base contract
  // number but carries more detail than the stale staging field.
  if (sameRoot && getContractNumberSpecificity(extracted) > getContractNumberSpecificity(parsed)) {
    return extracted
  }

  return parsed
}

// "Роялти по ДКК № 845-Н-ПП от 24.03.2026 ..." → "2026-03-24".
// We look for a date that immediately follows the «от» keyword (case-insensitive).
// If the purpose contains several «от <date>» fragments (e.g. «от ИП ... от 24.03.2026»),
// the first chronologically valid one is returned; the contract date is always the
// first such date in practice.
export function extractContractSignDateIso(purpose: string | null | undefined): string | null {
  if (!purpose) return null
  const match = purpose.match(/(?:^|[^\p{L}\p{N}_])от\s+(\d{1,2})\.(\d{1,2})\.(\d{4})(?![\p{L}\p{N}_])/iu)
  if (!match) return null
  const day = parseInt(match[1], 10)
  const month = parseInt(match[2], 10)
  const year = parseInt(match[3], 10)
  if (!day || !month || !year) return null
  if (month < 1 || month > 12) return null
  if (day < 1 || day > 31) return null
  const dd = String(day).padStart(2, '0')
  const mm = String(month).padStart(2, '0')
  return `${year}-${mm}-${dd}`
}

export function extractBeneficiaryInnFromPurpose(purpose: string | null | undefined): string | null {
  if (!purpose) return null

  const matches = Array.from(
    purpose.matchAll(/(?:от\s+)?(?:инн|унп|унн)\s*(\d{6,12})/gi),
    (match) => normalizeInnForMatching(match[1])
  ).filter(Boolean)

  if (matches.length === 0) return null
  return matches[0] ?? null
}

export interface PurposeAmountCurrency {
  currency: string
  amount: number
  crossRate: number | null
  incomingAmount: number | null
}

function parsePurposeAmountValue(raw: string): number | null {
  const normalized = raw.replace(/\s/g, '').replace(',', '.')
  if (!normalized) return null
  const parsed = Number.parseFloat(normalized)
  return Number.isFinite(parsed) ? parsed : null
}

export function extractPurposeAmountCurrency(purpose: string | null | undefined): PurposeAmountCurrency | null {
  if (!purpose) return null

  // Each pattern carries the capture-group index of currency / amount / cross-rate,
  // because the two purpose formats put them in a different order:
  //   "EUR 100 @ 1.1"  -> currency, amount, rate
  //   "100 @ 1.1 EUR"  -> amount, rate, currency
  const patterns: Array<{ re: RegExp; currency: number; amount: number; rate: number }> = [
    { re: /(?:^|[^\p{L}\p{N}])([A-Z]{3})\s*([\d][\d\s.,]*)\s*@\s*([\d][\d\s.,]*)/iu, currency: 1, amount: 2, rate: 3 },
    { re: /(?:^|[^\p{L}\p{N}])([\d][\d\s.,]*)\s*@\s*([\d][\d\s.,]*)\s*([A-Z]{3})/iu, currency: 3, amount: 1, rate: 2 },
  ]

  for (const pattern of patterns) {
    const match = purpose.match(pattern.re)
    if (!match) continue

    const currency = (match[pattern.currency] ?? '').trim().toUpperCase()
    const amount = parsePurposeAmountValue(match[pattern.amount] ?? '')
    const crossRate = parsePurposeAmountValue(match[pattern.rate] ?? '')
    if (!currency || amount == null) continue

    return {
      currency,
      amount,
      crossRate,
      incomingAmount: crossRate != null ? Math.round((amount * crossRate) * 100) / 100 : null,
    }
  }

  return null
}

export function resolveEffectiveInnForMatching(input: {
  payerInn: string | null | undefined
  paymentPurpose: string | null | undefined
}): string {
  if (isJamesBilgtonInn(input.payerInn)) {
    return extractBeneficiaryInnFromPurpose(input.paymentPurpose) ?? normalizeInnForMatching(input.payerInn)
  }

  return normalizeInnForMatching(input.payerInn)
}

export function getPaymentMatchingContext(input: {
  payerInn: string | null | undefined
  paymentPurpose: string | null | undefined
  counterparty?: string | null | undefined
  parsedDkkNumber?: string | null | undefined
}) {
  const payerInn = normalizeInnForMatching(input.payerInn) || (extractBankIdentifierFromText(input.counterparty) ?? '')
  const beneficiaryInn = extractBeneficiaryInnFromPurpose(input.paymentPurpose)
  const effectiveInn = resolveEffectiveInnForMatching({
    payerInn,
    paymentPurpose: input.paymentPurpose,
  })

  return {
    payerInn,
    beneficiaryInn,
    effectiveInn,
    isJamesBilgtonPayment: isJamesBilgtonInn(payerInn),
    contractNumberForMatching: resolveDkkForMatching(input.parsedDkkNumber, input.paymentPurpose),
    // Идентификаторы плательщика по убыванию надёжности. У нерезидента в колонке
    // ИНН приходит номер счёта (в белорусской выписке ИНН нет вовсе), а налоговый
    // номер лежит в тексте назначения или в имени контрагента — поэтому матч
    // перебирает кандидатов, а не упирается в один effectiveInn.
    innCandidates: buildInnCandidates({
      effectiveInn,
      payerInn,
      beneficiaryInn,
      counterparty: input.counterparty,
    }),
  }
}

/**
 * Похоже ли значение на налоговый номер: 9 цифр (УНП РБ), 10 (ЮЛ РФ), 12 (ИП РФ).
 * Зеркало серверного `royalty_is_valid_tax_id`; правило одно на обе стороны.
 */
export function isTaxIdLike(value: string | null | undefined): boolean {
  const digits = (value ?? '').replace(/\D/g, '')
  return digits.length === 9 || digits.length === 10 || digits.length === 12
}

function buildInnCandidates(input: {
  effectiveInn: string
  payerInn: string
  beneficiaryInn: string | null
  counterparty?: string | null | undefined
}): string[] {
  const primary = normalizeInnForMatching(input.effectiveInn)

  // Резервные кандидаты берутся ТОЛЬКО когда основной идентификатор — не
  // налоговый номер, то есть номер счёта нерезидента (в белорусской выписке ИНН
  // нет вовсе). Если плательщик опознан валидным ИНН, подменять его номером из
  // текста нельзя: замер 03.09.2026 на 22 729 строках показал, что так матч
  // уходил бы на договор до пролонгации (ДКК «49-П-Н» в назначении при живом
  // «49-П2-Н»), тогда как оператор из раза в раз выбирал действующий договор.
  if (isTaxIdLike(primary)) return [primary]

  const ordered = [
    primary,
    normalizeInnForMatching(input.payerInn),
    normalizeInnForMatching(input.beneficiaryInn ?? ''),
    normalizeInnForMatching(extractBelarusTaxIdFromText(input.counterparty) ?? ''),
  ].filter(Boolean)

  return Array.from(new Set(ordered))
}

/**
 * Идентификатор плательщика, по которому матч и надо вести: первый кандидат,
 * у которого в справочнике вообще есть договоры. Единственная реализация правила
 * перебора — очередь разноски, импорт и инференс партнёра обязаны выбирать один
 * и тот же ключ, иначе один и тот же платёж матчится по-разному на разных экранах.
 *
 * Ничего не нашлось — возвращается `effectiveInn`, то есть прежнее поведение:
 * строка уедет в ручной разбор с тем же ключом, что показывает интерфейс.
 */
export function resolveMatchingInn<C>(
  matching: { innCandidates: string[]; effectiveInn: string },
  contracts: C[],
  innList: (contract: C) => string[]
): string {
  const found = matching.innCandidates.find((candidate) =>
    contracts.some((contract) => innList(contract).includes(candidate))
  )
  return found ?? matching.effectiveInn
}

export const MONTH_SHORT = ['', 'Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн', 'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек']
export const MONTH_FULL = ['', 'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь']

// Подпись сегмента месяца для charge посегментного движка (миграция 20260705160000):
// «дд.мм–дд.мм», только если charge покрывает не весь месяц (иначе null — обычный
// месячный charge не нуждается в уточнении). Без неё два сегмента одного charge_type
// в месяце выглядят на UI одинаковыми строками.
export function chargeSegmentLabel(b: {
  year: number
  month: number
  segment_no?: number | null
  segment_start?: string | null
  segment_end?: string | null
}): string | null {
  if (!b.segment_start || !b.segment_end) return null
  const mm = String(b.month).padStart(2, '0')
  const monthStart = `${b.year}-${mm}-01`
  const lastDay = new Date(b.year, b.month, 0).getDate()
  const monthEnd = `${b.year}-${mm}-${String(lastDay).padStart(2, '0')}`
  if ((b.segment_no ?? 1) === 1 && b.segment_start <= monthStart && b.segment_end >= monthEnd) return null
  const fmt = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}`
  return `${fmt(b.segment_start)}–${fmt(b.segment_end)}`
}

// Человекочитаемые подписи «карманов начисления» (component charge_type). Для REGULAR
// единственный карман — ROYALTY; для SUBSCRIPTION в одном месяце сосуществуют четыре
// независимых кармана. Раньше жили локально в StudioRoyaltiesPage — вынесены сюда, чтобы
// платёжные экраны/модалки разноски показывали один и тот же ярлык (P-23).
export const CHARGE_TYPE_LABEL: Record<RoyaltyChargeType, string> = {
  ROYALTY:        'Роялти',
  SPLIT_ROYALTY:  'Роялти (сплит)',
  SPLIT_AGENCY:   'Агентское (сплит)',
  MANUAL_ROYALTY: 'Роялти (ручные)',
  LISTOK_CRM:     'Ручное агентское',
  FIXED_ROYALTY:  'Фиксированная часть роялти',
}

// Только цвет текста — для строк, где ярлык кармана стоит рядом с суммой начисления.
export const CHARGE_TYPE_COLOR: Record<RoyaltyChargeType, string> = {
  ROYALTY:        'text-foreground',
  SPLIT_ROYALTY:  'text-blue-600',
  SPLIT_AGENCY:   'text-blue-500',
  MANUAL_ROYALTY: 'text-amber-700',
  LISTOK_CRM:     'text-emerald-700',
  FIXED_ROYALTY:  'text-violet-700',
}

// Пилюля (bg + text) для компактного бейджа кармана в строках платежей/разноски.
export const CHARGE_TYPE_BADGE_CLASS: Record<RoyaltyChargeType, string> = {
  ROYALTY:        'bg-muted text-foreground',
  SPLIT_ROYALTY:  'bg-blue-100 text-blue-700',
  SPLIT_AGENCY:   'bg-blue-100 text-blue-600',
  MANUAL_ROYALTY: 'bg-amber-100 text-amber-700',
  LISTOK_CRM:     'bg-emerald-100 text-emerald-700',
  FIXED_ROYALTY:  'bg-violet-100 text-violet-700',
}

// Identifies a partner for a single bank-statement row using ИНН and contract number from the purpose field.
// Used by both the "discover gaps" (group queue by partner) and inline manual matching flows.
export interface PartnerInferenceInput {
  inn: string | null | undefined
  counterparty?: string | null | undefined
  parsed_dkk_number: string | null | undefined
  payment_purpose: string | null | undefined
}

export interface PartnerInferenceResult {
  // resolved partner (if any)
  partner_number: number | null
  persons_fio: string | null
  partner_inns: string[]
  // contracts that matched on inn/contract number (for picker UI)
  exact_contracts: Array<{ contract_id: number; series_id: number; contract_number: string | null }>
  inn_contracts_count: number
  contract_number_for_matching: string
}

// Normalized legal-name key — mirrors DB royalty_normalize_legal_name: keep
// letters/digits (latin + cyrillic), drop everything else (incl. spaces) so the
// bank's word-splitting ("L EVITATION") collapses to one key.
function normalizeLegalNameKeyForMatching(value: string | null | undefined): string {
  return String(value ?? '').toUpperCase().replace(/[^A-Z0-9А-ЯЁ]+/g, '')
}

export function inferPartnerForStagingRow<C extends {
  contract_id: number
  series_id: number
  partner_number: number
  persons_fio: string | null
  contract_number: string | null
  partner_inn_list: string[]
  contract_inn_list: string[]
  partner_legal_name_keys?: string[]
}>(input: PartnerInferenceInput, contracts: C[]): PartnerInferenceResult {
  const matching = getPaymentMatchingContext({
    payerInn: input.inn,
    paymentPurpose: input.payment_purpose,
    counterparty: input.counterparty,
    parsedDkkNumber: input.parsed_dkk_number,
  })
  const inn = resolveMatchingInn(matching, contracts, (contract) => contract.partner_inn_list)
  const dkk = matching.contractNumberForMatching
  const normalizedDkk = normalizeContractNumberForMatching(dkk)

  const innContracts = inn ? contracts.filter((c) => c.partner_inn_list.includes(inn)) : []
  const contractsByTextNumber = normalizedDkk
    ? contracts.filter((c) =>
        normalizeContractNumberForMatching(c.contract_number) === normalizedDkk
      )
    : []

  // Prefer strict entity-level match, but allow a partner-level fallback when
  // the payer/beneficiary INN resolves to the partner and the contract number is unique there.
  const strictExact = (inn && normalizedDkk)
    ? contracts.filter((c) =>
        c.contract_inn_list.includes(inn)
        && normalizeContractNumberForMatching(c.contract_number) === normalizedDkk
      )
    : []

  const partnerLevelExact = (normalizedDkk && innContracts.length > 0)
    ? innContracts.filter((c) =>
        normalizeContractNumberForMatching(c.contract_number) === normalizedDkk
      )
    : []

  const exactRaw = strictExact.length > 0
    ? strictExact
    : Array.from(new Map(partnerLevelExact.map((c) => [c.contract_id, c])).values())

  // Pick partner: from exact, else from inn, else from contract number, else —
  // for foreign rows with no INN — from a legal entity bound by payer name.
  const legalNameKey = normalizeLegalNameKeyForMatching(input.counterparty)
  const legalNameContract = legalNameKey
    ? contracts.find((c) => (c.partner_legal_name_keys ?? []).includes(legalNameKey)) ?? null
    : null
  const partnerSource = exactRaw[0] ?? innContracts[0] ?? contractsByTextNumber[0] ?? legalNameContract ?? null

  return {
    partner_number: partnerSource?.partner_number ?? null,
    persons_fio: partnerSource?.persons_fio ?? null,
    partner_inns: Array.from(
      new Set(
        (partnerSource ? contracts.filter((c) => c.partner_number === partnerSource.partner_number) : [])
          .flatMap((c) => c.partner_inn_list)
      )
    ),
    exact_contracts: exactRaw.map((c) => ({
      contract_id: c.contract_id,
      series_id: c.series_id,
      contract_number: c.contract_number,
    })),
    inn_contracts_count: innContracts.length,
    contract_number_for_matching: dkk,
  }
}

/**
 * Сколько денег платежа (в валюте платежа) ушло в эту разноску — зеркало серверной
 * `royalty_payment_allocation_native_amount`, по которой считается `remaining_amount`.
 * Масштабировать разноски к полной сумме платежа нельзя: у частично разнесённого
 * платежа это рисует остаток нулём (платёж 20106: 50,74 из 68,44 ₽ показывались как 68,44).
 */
export function getAllocationAmountInPaymentCurrency(
  payment: { currency_code: string | null; fx_rate: number | null },
  allocation: {
    allocated_amount: number | null
    allocated_currency_code: string | null
    allocated_amount_rub: number | null
  }
): number {
  const paymentCurrency = payment.currency_code?.trim() || 'RUB'
  const round2 = (value: number) => Math.round(value * 100) / 100
  if ((allocation.allocated_currency_code?.trim() || 'RUB') === paymentCurrency) {
    return round2(Number(allocation.allocated_amount ?? 0))
  }
  const amountRub = Number(allocation.allocated_amount_rub ?? 0)
  const fxRate = Number(payment.fx_rate ?? 0)
  if (paymentCurrency === 'RUB' || fxRate <= 0) return round2(amountRub)
  return round2(amountRub / fxRate)
}
